(ns app.front.view.case-note.subs
  (:require [app.domain.case-notes.model :as case-notes-model]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.front.process :as process]
            [app.front.util :as futil]
            [app.front.view.workspace.subs :as ws-subs]
            [core.front.date-util :as date-util]
            [core.front.feature-flags :as ff]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))

(defn- clear-state
  [db]
  (dissoc db ::state))

(defn- db->content
  [db]
  (get-in db [::state :content]))

(rf/reg-event-fx ::begin-creating-note
  (fn [{:keys [db]} _]
    {:db (-> db
             (assoc-in [::state :creating-new?] true)
             ;; Don't focus other notes while adding a new one
             (assoc-in [::state :lock-focus] :new-note))}))

(rf/reg-event-fx ::trigger-add-case-note
  (fn [{:keys [db]} _]
    {:db (-> db
             (assoc-in [::state :creating-new?] true)
             (assoc-in [::state :lock-focus] :new-note))
     :fx [[::futil/scroll-to-section :case-notes-section]]}))

(rf/reg-event-fx ::note-content-changed
  (fn [{:keys [db]} [_ content]]
    {:pre [(string? content)]}
    {:db (assoc-in db [::state :content] content)}))

(rf/reg-event-fx ::save-new-note
  (fn [{:keys [db]} [_ current-phase-id]]
    {:pre [(integer? current-phase-id)]}
    (let [content (db->content db)]
      {:fx [[:dispatch [::process/signal
                        current-phase-id
                        [:process.signal/add-case-note {:content content}]
                        {:notify  {:success :case-note/add-success}
                         :on-done [::clear-changes]}]]]})))

(rf/reg-event-fx ::clear-changes
  (fn [{:keys [db]} _]
    {:db (clear-state db)}))

(rf/reg-event-fx ::note-focused
  (fn [{:keys [db]} [_ id]]
    {:pre [(integer? id)]}
    {:db (assoc-in db [::state :focus] id)}))

(rf/reg-event-fx ::note-unfocused
  (fn [{:keys [db]} _]
    {:db (update db ::state dissoc :focus)}))

(rf/reg-event-fx ::edit-button-clicked
  (fn [{:keys [db]} [_ id current-content]]
    {:pre [(integer? id) (string? current-content)]}
    {:db (update db ::state merge {:content    current-content
                                   :editing    id
                                   :lock-focus id})}))

(rf/reg-event-fx ::save-edited-changes
  (fn [{:keys [db]} [_ note-phase-id id]]
    {:pre [(integer? note-phase-id) (integer? id)]}
    (let [content (db->content db)]
      {:fx [[:dispatch [::process/signal
                        note-phase-id
                        [:process.signal/edit-case-note {:case-note-id id
                                                         :content      content}]
                        {:notify  {:success :case-note/edit-success}
                         :on-done [::cancel-editing-note]}]]]})))

(rf/reg-event-fx ::delete-button-clicked
  (fn [{:keys [db]} [_ note-phase-id id]]
    {:pre [(integer? note-phase-id) (integer? id)]}
    {:db (assoc-in db [::state :show-delete] (util/mmap note-phase-id id))}))

(rf/reg-event-fx ::delete-cancelled
  (fn [{:keys [db]} _]
    {:db (update db ::state dissoc :show-delete)}))

(rf/reg-event-fx ::delete-confirmed
  (fn [_ [_ note-phase-id id]]
    {:pre [(integer? note-phase-id) (integer? id)]}
    {:fx [[:dispatch [::process/signal
                      note-phase-id
                      [:process.signal/delete-case-note {:case-note-id id}]
                      {:notify {:success :case-note/delete-success}}]]]}))

(rf/reg-event-fx ::cancel-editing-note
  (fn [{:keys [db]}]
    {:db (update db ::state dissoc :lock-focus :content :editing)}))

(rf/reg-event-fx ::show-more-clicked
  (fn [{:keys [db]} [_ id]]
    {:pre [(integer? id)]}
    {:db (assoc-in db [::state :expanded] id)}))

(rf/reg-event-fx ::show-less-clicked
  (fn [{:keys [db]} _]
    {:db (update db ::state dissoc :expanded)}))

;;; Subscriptions

(rf/reg-sub ::state
  (fn [db _]
    (::state db {})))

(rf/reg-sub ::local-content
  :<- [::state]
  :-> :content)

(rf/reg-sub ::creating-new-note?
  :<- [::state]
  (fn [state _]
    (-> state :creating-new? boolean)))

(rf/reg-sub ::has-local-content?
  :<- [::local-content]
  (fn [content]
    (-> content seq boolean)))

(rf/reg-sub ::editing-note?
  :<- [::state]
  (fn [state [_ id]]
    {:pre [(integer? id)]}
    (= (-> state :editing) id)))

(rf/reg-sub ::edits-made?
  (fn [[_ id]]
    {:pre [(integer? id)]}
    [(rf/subscribe [::editing-note? id])
     (rf/subscribe [::local-content])
     (rf/subscribe [::content id])])
  (fn [[editing? local-content original-content]]
    (and editing?
         (not= local-content original-content))))

(rf/reg-sub ::delete-modal-data
  :<- [::state]
  :-> :show-delete)

(rf/reg-sub ::show-delete-modal?
  :<- [::delete-modal-data]
  :-> some?)

(rf/reg-sub ::focused-note
  :<- [::state]
  (fn [state]
    (or (:lock-focus state)
        (:focus state))))

(rf/reg-sub ::note-focused?
  :<- [::focused-note]
  (fn [focused [_ id]]
    {:pre [(integer? id)]}
    (= focused id)))

(rf/reg-sub ::note-expanded?
  :<- [::state]
  (fn [state [_ id]]
    {:pre [(integer? id)]}
    (= id (-> state :expanded))))

(rf/reg-sub ::tr
  :<- [::ws-subs/tr]
  :-> identity)

(rf/reg-sub ::case-notes
  :<- [::ws-subs/workspace]
  (fn [ws _]
    (->> ws
         workspace-model/phase-entities
         (mapcat case-notes-model/phase-case-notes)
         du/entities->entity-db)))

(rf/reg-sub ::has-case-notes?
  :<- [::case-notes]
  (fn [notes _]
    (-> notes seq boolean)))

(rf/reg-sub ::can-add?
  (fn [[_ current-phase-id]]
    [(rf/subscribe [::process/signal-permitted? current-phase-id [:process.signal/add-case-note]])
     (rf/subscribe [::has-local-content?])])
  (fn [[can-add? has-edits?] _] (and (not has-edits?) can-add?)))

(rf/reg-sub ::show-case-notes-section?
  (fn [[_ current-phase-id]]
    [(rf/subscribe [::has-case-notes?])
     (rf/subscribe [::can-add? current-phase-id])
     (rf/subscribe [::has-local-content?])
     (rf/subscribe [::ff/enabled? :feature-flag.id/inspection-ui-improvements])
     (rf/subscribe [::creating-new-note?])])
  (fn [[has-notes? add-permitted? has-edits? ui-improvements? creating?] _]
    (if ui-improvements?
      (or has-notes? has-edits? creating?)
      (or has-notes? add-permitted? has-edits?))))

(defn- note->latest-change-timestamp
  [note]
  (or (-> note :latest-change :ts)
      (-> note :entity/created)))

(defn- note->latest-change-user
  [note]
  (or (-> note :latest-change :user)
      (-> note :entity/creator user-model/full-name)))

(rf/reg-sub ::case-note-ids-sorted
  :<- [::case-notes]
  (fn [notes _]
    (->> notes
         vals
         (sort-by note->latest-change-timestamp >)
         (mapv (fn [note] {:id            (:db/id note)
                           :note-phase-id (-> note :entity/parent :db/id)})))))

(rf/reg-sub ::case-note
  :<- [::case-notes]
  (fn [notes [_ id]]
    (get notes id)))

(rf/reg-sub ::content
  (fn [[_ id]]
    {:pre [(integer? id)]}
    (rf/subscribe [::case-note id]))
  :-> :case-note/content)

;; TODO: perhaps move these to a util ns
(defn- json-str->edn
  [s]
  (try
    (-> (. js/JSON parse s)
        (js->clj :keywordize-keys true))
    (catch js/Error _ ;; In case of HTML we don't truncate, the editor will do it.
      s)))

(defn- edn->json-str
  [data]
  (->> data
       clj->js
       (. js/JSON stringify)))

(rf/reg-sub ::content-parsed
  (fn [[_ id]]
    {:pre [(integer? id)]}
    (rf/subscribe [::content id]))
  (fn [content]
    (-> content json-str->edn)))

(rf/reg-sub ::row-count
  (fn [[_ id]]
    {:pre [(integer? id)]}
    (rf/subscribe [::content-parsed id]))
  (fn [content]
    (-> content :blocks count)))

(def max-lines-to-show 5)

(rf/reg-sub ::truncate-content?
  (fn [[_ id]]
    {:pre [(integer? id)]}
    (rf/subscribe [::row-count id]))
  (fn [rows]
    (> rows max-lines-to-show)))

(rf/reg-sub ::truncated-content
  (fn [[_ id]]
    {:pre [(integer? id)]}
    (rf/subscribe [::content-parsed id]))
  (fn [content]
    (-> content
        ;; Note that we take N logical lines, which may be a lot longer
        ;; than N visible lines.
        (update :blocks (fn [blocks] (->> blocks
                                          (take max-lines-to-show)
                                          (into []))))
        edn->json-str)))

(rf/reg-sub ::show-truncated-content?
  (fn [[_ id]]
    {:pre [(integer? id)]}
    [(rf/subscribe [::truncate-content? id])
     (rf/subscribe [::editing-note? id])
     (rf/subscribe [::note-expanded? id])])
  (fn [[truncate? editing? expanded?] _]
    (and truncate?
         (not editing?)
         (not expanded?))))

(rf/reg-sub ::visible-content
  (fn [[_ id]]
    {:pre [(integer? id)]}
    (rf/subscribe [::show-truncated-content? id]))
  (fn [show-truncated? [_ id]]
    (if show-truncated?
      @(rf/subscribe [::truncated-content id])
      @(rf/subscribe [::content id]))))

;; The `app.front.ui.rich-text/editor` does not refresh when the content changes
;; (e.g. when "show more" is clicked) so we must force re-render using :key metadata
(rf/reg-sub ::editor-hash
  (fn [[_ id]]
    {:pre [(integer? id)]}
    (rf/subscribe [::visible-content id]))
  :-> hash)

(rf/reg-sub ::edited?
  (fn [[_ id]]
    {:pre [(integer? id)]}
    (rf/subscribe [::case-note id]))
  (fn [note]
    (-> note :latest-change some?)))

(rf/reg-sub ::copied?
  (fn [[_ id]]
    {:pre [(integer? id)]}
    (rf/subscribe [::case-note id]))
  (fn [note]
    (-> note :case-note/copied-from-workspace some?)))

(rf/reg-sub ::last-modified-by
  (fn [[_ id]]
    {:pre [(integer? id)]}
    (rf/subscribe [::case-note id]))
  :-> note->latest-change-user)

(rf/reg-sub ::last-modified-timestamp
  (fn [[_ id]]
    {:pre [(integer? id)]}
    [(rf/subscribe [::case-note id]) (rf/subscribe [::date-util/date-time-fn])])
  (fn [[note format-date-time]]
    (-> note note->latest-change-timestamp format-date-time)))
